export type TranscriptArtifactStatus = "unavailable" | "raw" | "redacted" | "forbidden";
export type TranscriptRedactionStatus = "not_started" | "pending" | "redacted" | "failed" | "forbidden";
export type TranscriptVisibility = "staff_only" | "coach_only" | "company_redacted" | "participant_forbidden";
export type TranscriptAccessMode = "raw" | "redacted" | "forbidden" | "unavailable";

export interface LiveTranscript {
  sessionId: string | null;
  text: string;
  available: boolean;
  artifactStatus: TranscriptArtifactStatus;
  redactionStatus: TranscriptRedactionStatus;
  visibility: TranscriptVisibility;
  accessMode: TranscriptAccessMode;
}

function stringValue(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return null;
}

function enumValue<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  const candidate = stringValue(value);
  return candidate && allowed.includes(candidate as T) ? (candidate as T) : fallback;
}

const artifactStatuses = ["unavailable", "raw", "redacted", "forbidden"] as const;
const redactionStatuses = ["not_started", "pending", "redacted", "failed", "forbidden"] as const;
const visibilities = ["staff_only", "coach_only", "company_redacted", "participant_forbidden"] as const;
const accessModes = ["raw", "redacted", "forbidden", "unavailable"] as const;

/** Normalize the live API's `{ transcript: {...} }` response without inventing text. */
export function normalizeLiveTranscript(payload: unknown): LiveTranscript {
  const root = payload !== null && typeof payload === "object" ? payload as { transcript?: unknown } : {};
  const source = root.transcript !== null && typeof root.transcript === "object"
    ? root.transcript as Record<string, unknown>
    : root as Record<string, unknown>;
  const text = typeof source.text === "string" ? source.text : "";
  const artifactStatus = enumValue(source.artifactStatus ?? source.artifact_status, artifactStatuses, "unavailable");
  const redactionStatus = enumValue(source.redactionStatus ?? source.redaction_status, redactionStatuses, "not_started");
  const visibility = enumValue(source.visibility, visibilities, "staff_only");
  const accessMode = enumValue(source.accessMode ?? source.access_mode, accessModes, "unavailable");
  return {
    sessionId: stringValue(source.sessionId ?? source.session_id),
    text,
    available: source.available === true && text.length > 0,
    artifactStatus,
    redactionStatus,
    visibility,
    accessMode
  };
}
